/** Safely fetches private chat attachments and owns their session-scoped blob URL lifecycle. */
import { API_BASE, getAuthHeaderToken } from '../utils/supabase/client';
import { publicAnonKey } from '../utils/supabase/info';

const PRIVATE_CHAT_ATTACHMENT_PATH = '/chat/attachment/';
const API_URL = new URL(API_BASE);
const API_ATTACHMENT_PREFIX = `${API_URL.pathname}${PRIVATE_CHAT_ATTACHMENT_PATH}`;

type AttachmentUrlClassification =
  | { kind: 'private'; url: string }
  | { kind: 'public'; url: string }
  | { kind: 'rejected'; reason: string };

interface ObjectUrlEntry {
  objectUrl: string;
  refs: number;
}

export interface AttachmentObjectUrlLease {
  url: string;
  release: () => void;
}

const objectUrlCache = new Map<string, ObjectUrlEntry>();
const inFlight = new Map<string, Promise<ObjectUrlEntry>>();
let activeAuthScope: string | null = null;

const resolveCandidateUrl = (url: string): URL => {
  // Upload responses are normally absolute, but accepting this documented
  // edge-relative form keeps older cached message rows usable.
  if (url.startsWith(PRIVATE_CHAT_ATTACHMENT_PATH)) {
    return new URL(`${API_BASE}${url}`);
  }
  const base = typeof window === 'undefined' ? API_BASE : window.location.origin;
  return new URL(url, base);
};

// The edge runtime sees its own request as `http://<host>/<function>/...` (TLS
// and the `/functions/v1` prefix are stripped upstream), so uploads made before
// the server fix stored that internal form. Only on the API's own hostname,
// rebuild it into the canonical HTTPS endpoint so those images still load.
const FUNCTION_PATH = API_URL.pathname.replace(/^\/functions\/v1/, '');
const canonicaliseInternalApiUrl = (parsed: URL): URL => {
  if (parsed.hostname !== API_URL.hostname) return parsed;
  if (parsed.origin === API_URL.origin && parsed.pathname.startsWith(API_URL.pathname)) {
    return parsed;
  }
  const internalPrefix = `${FUNCTION_PATH}${PRIVATE_CHAT_ATTACHMENT_PATH}`;
  if (!parsed.pathname.startsWith(internalPrefix)) return parsed;
  const canonical = new URL(`${API_URL.origin}${API_URL.pathname}${parsed.pathname.slice(FUNCTION_PATH.length)}`);
  canonical.search = parsed.search;
  return canonical;
};

const classifyAttachmentUrl = (url: string): AttachmentUrlClassification => {
  if (!url) return { kind: 'rejected', reason: 'Attachment URL is missing' };

  let parsed: URL;
  try {
    parsed = canonicaliseInternalApiUrl(resolveCandidateUrl(url));
  } catch {
    return { kind: 'rejected', reason: 'Attachment URL is invalid' };
  }

  const looksLikePrivateAttachment = parsed.pathname.includes(PRIVATE_CHAT_ATTACHMENT_PATH);
  if (!looksLikePrivateAttachment) return { kind: 'public', url: parsed.toString() };

  const fileId = parsed.pathname.slice(API_ATTACHMENT_PREFIX.length);
  const hasExactPath =
    parsed.pathname.startsWith(API_ATTACHMENT_PREFIX) &&
    fileId.length > 0 &&
    !fileId.includes('/');
  const hasExpectedQuery = Boolean(parsed.searchParams.get('chatId'));
  if (parsed.origin !== API_URL.origin || !hasExactPath || !hasExpectedQuery) {
    return {
      kind: 'rejected',
      reason: 'Private attachment URL does not match the Quantis API endpoint',
    };
  }

  return { kind: 'private', url: parsed.toString() };
};

export const isPrivateChatAttachmentUrl = (url: string): boolean =>
  classifyAttachmentUrl(url).kind === 'private';

const markScopeStale = (nextScope: string | null): void => {
  if (activeAuthScope === nextScope) return;
  activeAuthScope = nextScope;
  for (const [key, entry] of objectUrlCache) {
    if (entry.refs === 0) {
      URL.revokeObjectURL(entry.objectUrl);
      objectUrlCache.delete(key);
    }
  }
};

const releaseEntry = (key: string, entry: ObjectUrlEntry): void => {
  if (entry.refs > 0) entry.refs -= 1;
  if (entry.refs === 0) {
    URL.revokeObjectURL(entry.objectUrl);
    if (objectUrlCache.get(key) === entry) objectUrlCache.delete(key);
  }
};

const requireUserToken = async (): Promise<string> => {
  const token = await getAuthHeaderToken();
  if (!token || token === publicAnonKey) {
    throw new Error('An authenticated session is required to access this attachment');
  }
  return token;
};

export class AuthenticatedAttachmentService {
  static clearForSessionChange(): void {
    markScopeStale(null);
  }

  static async fetchBlob(url: string): Promise<Blob> {
    const classification = classifyAttachmentUrl(url);
    if (classification.kind === 'rejected') throw new Error(classification.reason);

    let headers: HeadersInit | undefined;
    if (classification.kind === 'private') {
      const token = await requireUserToken();
      headers = { Authorization: `Bearer ${token}` };
    }
    const response = await fetch(classification.url, { headers });
    if (!response.ok) {
      throw new Error(`Attachment request failed (HTTP ${response.status})`);
    }
    return response.blob();
  }

  static async acquireObjectUrl(url: string): Promise<AttachmentObjectUrlLease> {
    const classification = classifyAttachmentUrl(url);
    if (classification.kind === 'rejected') throw new Error(classification.reason);
    if (classification.kind === 'public') {
      return { url: classification.url, release: () => {} };
    }

    const token = await requireUserToken();
    markScopeStale(token);
    const privateUrl = classification.url;
    const key = `${token}\n${privateUrl}`;
    let entry = objectUrlCache.get(key);
    if (!entry) {
      let pending = inFlight.get(key);
      if (!pending) {
        pending = fetch(privateUrl, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(async (response) => {
            if (!response.ok) {
              throw new Error(`Attachment request failed (HTTP ${response.status})`);
            }
            const created: ObjectUrlEntry = {
              objectUrl: URL.createObjectURL(await response.blob()),
              refs: 0,
            };
            objectUrlCache.set(key, created);
            return created;
          })
          .finally(() => inFlight.delete(key));
        inFlight.set(key, pending);
      }
      entry = await pending;
    }

    entry.refs += 1;
    let released = false;
    return {
      url: entry.objectUrl,
      release: () => {
        if (released) return;
        released = true;
        releaseEntry(key, entry!);
      },
    };
  }

  static async open(url: string): Promise<boolean> {
    const classification = classifyAttachmentUrl(url);
    if (classification.kind === 'rejected') {
      console.error('[AuthenticatedAttachmentService] Refused unsafe attachment URL');
      return false;
    }

    if (classification.kind === 'public') {
      window.open(classification.url, '_blank', 'noopener,noreferrer');
      return true;
    }

    // Open synchronously so browsers do not classify the eventual navigation
    // after the authenticated fetch as an unsolicited popup.
    const tab = window.open('about:blank', '_blank');
    if (!tab) return false;
    tab.opener = null;
    try {
      const lease = await this.acquireObjectUrl(classification.url);
      tab.location.href = lease.url;
      window.setTimeout(lease.release, 60_000);
      return true;
    } catch (error) {
      tab.close();
      console.error('[AuthenticatedAttachmentService] Unable to open attachment:', error);
      return false;
    }
  }

  static async download(url: string, fileName: string): Promise<void> {
    const blob = await this.fetchBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName || 'attachment';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Some browsers are still reading the blob when click() returns.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }

  /** Test-only state reset; production callers should use clearForSessionChange. */
  static __resetForTests(): void {
    for (const entry of objectUrlCache.values()) URL.revokeObjectURL(entry.objectUrl);
    objectUrlCache.clear();
    inFlight.clear();
    activeAuthScope = null;
  }
}
